import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FiUpload, FiRefreshCw, FiTrash2, FiFileText } from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import { useLibrary } from '../context/LibraryContext';
import { useReports } from '../context/ReportContext';
import {
  ACTIVE_OPERATIONS_CACHE_TTL_MS,
  normalizeOperationType,
  filterOperationsByReports
} from '../utils/operationHelpers';
import './OperationsIndicator.css';

const normalizeFilename = (filename) => {
  if (!filename) return '';
  // Extract just the basename, remove paths
  let name = filename.split('/').pop().split('\\').pop().trim().toLowerCase();

  // Backend transforms filenames: replaces spaces and special chars with underscores
  // Normalize by removing all underscores, spaces, and parentheses to match variations
  // This handles: "file (2).pdf" vs "file__2_.pdf" vs "file_2.pdf"
  name = name.replace(/[\s_()]+/g, '');

  return name;
};

const getOperationKey = (operation) => {
  const docKey = operation.document_id || operation.documentId;
  const nameKey = operation.filename || operation.name;
  const runKey = operation.run_id || operation.runId;
  const type = operation.type || 'unknown';
  const isFileOperation = type.startsWith('file_');

  // For file operations, group by normalized filename to show single operation per document
  // This ensures all stages of a document's processing (upload/processing/splitting/kb_sync)
  // appear as a single operation, even if backend creates multiple operations for split chunks
  // We use filename instead of document_id because split chunks have different document_ids
  // but should still be shown as a single operation to the user
  if (isFileOperation && nameKey) {
    const normalizedName = normalizeFilename(nameKey);
    return `file:${normalizedName}`;
  }

  // For non-file operations (like reports), include type to differentiate
  const base = docKey || nameKey || runKey || operation.id || operation.operation_id;
  return `${type}:${base || ''}`;
};

const buildDocumentOperations = (documents) => {
  if (!Array.isArray(documents)) return [];

  return documents.reduce((acc, doc) => {
    if (!doc) return acc;

    const status = doc.status;
    const kbStatus = doc.kbStatus || doc.kb_status;
    let type = null;

    if (status === 'uploading') {
      type = 'file_upload';
    } else if (status === 'processing' || status === 'splitting' || status === 'split_pending') {
      type = 'file_processing';
    } else if (status === 'deleting') {
      type = 'file_deleting';
    } else if (kbStatus === 'syncing') {
      type = 'kb_syncing';
    }

    if (!type) return acc;

    const documentId = doc.id || doc.documentId || doc.document_id;

    acc.push({
      id: `doc-${documentId || doc.name}-${type}`,
      document_id: documentId,
      type,
      filename: doc.name || doc.filename,
      progress: status === 'deleting' ? doc.deletionProgress : undefined
    });

    return acc;
  }, []);
};

export default function OperationsIndicator() {
  const { fetchActiveOperations } = useApi();
  const { documents, refreshDocuments } = useLibrary();
  const { reports = [], refreshReports } = useReports();
  const [operations, setOperations] = useState(() => {
    try {
      const cached = sessionStorage.getItem('activeOperationsCache');
      if (!cached) return [];
      const parsed = JSON.parse(cached);
      const isFresh = parsed?.ts && Date.now() - parsed.ts < ACTIVE_OPERATIONS_CACHE_TTL_MS;
      return isFresh && Array.isArray(parsed.ops) ? parsed.ops : [];
    } catch {
      return [];
    }
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const lastRefreshRef = useRef(0);

  const loadOperations = useCallback(async () => {
    try {
      const data = await fetchActiveOperations();
      const ops = data.operations || [];

      setOperations(ops);

      // Clear cache if no operations, otherwise update it
      if (ops.length === 0) {
        try {
          sessionStorage.removeItem('activeOperationsCache');
        } catch {
          // Ignore cache clear failures
        }
      } else {
        try {
          sessionStorage.setItem('activeOperationsCache', JSON.stringify({
            ts: Date.now(),
            ops
          }));
        } catch {
          // Ignore cache write failures
        }
      }
    } catch (error) {
      console.error('Failed to fetch active operations:', error);
      // Clear both state and cache on error
      setOperations([]);
      try {
        sessionStorage.removeItem('activeOperationsCache');
      } catch {
        // Ignore cache clear failures
      }
    }
  }, [fetchActiveOperations]);

  // Poll for operations every 5 seconds
  useEffect(() => {
    loadOperations(); // Initial load

    const interval = setInterval(() => {
      loadOperations();
    }, 5000);

    // Listen for manual refresh events (e.g., when report generation starts)
    const handleRefresh = () => {
      console.log('[OperationsIndicator] Manual refresh triggered');
      loadOperations();
    };
    window.addEventListener('refreshOperations', handleRefresh);

    return () => {
      clearInterval(interval);
      window.removeEventListener('refreshOperations', handleRefresh);
    };
  }, [loadOperations]);

  const hasDocOperations = useMemo(() => documents?.some((doc) =>
    doc?.status === 'uploading'
    || doc?.status === 'processing'
    || doc?.status === 'splitting'
    || doc?.status === 'split_pending'
    || doc?.status === 'deleting'
    || doc?.status === 'kb_syncing'
    || doc?.kbStatus === 'syncing'
    || doc?.kb_status === 'syncing'
  ), [documents]);

  const filteredOperations = useMemo(
    () => filterOperationsByReports(operations, reports),
    [operations, reports]
  );

  const normalizedOperations = useMemo(
    () => filteredOperations.map((op) => ({ ...op, type: normalizeOperationType(op.type) })),
    [filteredOperations]
  );

  const documentOperations = useMemo(() => {
    return buildDocumentOperations(documents);
  }, [documents]);

  const combinedOperations = useMemo(() => {
    const merged = [];
    const seen = new Set();

    [...normalizedOperations, ...documentOperations].forEach((op) => {
      const key = getOperationKey(op);
      if (seen.has(key)) {
        return; // Skip duplicate
      }
      seen.add(key);
      merged.push(op);
    });

    return merged;
  }, [normalizedOperations, documentOperations]);

  // Detect stale document operations: If API shows no operations but documents show operations,
  // refresh documents to get latest status (operations may have completed)
  useEffect(() => {
    const hasApiOperations = normalizedOperations.length > 0;
    const hasOnlyDocOperations = !hasApiOperations && documentOperations.length > 0;

    if (hasOnlyDocOperations) {
      const now = Date.now();
      const timeSinceLastRefresh = now - lastRefreshRef.current;

      // Refresh documents if it's been more than 3 seconds since last refresh (reduced from 10s)
      // This ensures operation status messages clear more promptly after completion
      if (timeSinceLastRefresh > 3000) {
        lastRefreshRef.current = now;
        console.log('Refreshing documents to verify active operations...');
        refreshDocuments();
      }
    }
  }, [normalizedOperations, documentOperations, refreshDocuments]);

  // Refresh reports when report operations are detected or completed
  useEffect(() => {
    const hasReportOperations = normalizedOperations.some(
      op => op.type === 'report_generating' || op.type === 'report'
    );
    const hadReportOperations = window._hadReportOperations || false;

    // If we had report operations and now we don't, refresh reports immediately
    if (hadReportOperations && !hasReportOperations) {
      console.log('Report operation completed - refreshing reports list...');
      refreshReports();
    }

    window._hadReportOperations = hasReportOperations;
  }, [normalizedOperations, refreshReports]);

  // Force refresh when API operations complete to immediately clear status messages
  useEffect(() => {
    const hadOperations = window._hadApiOperations || false;
    const hasOperations = normalizedOperations.length > 0;

    // If we had operations and now we don't, force a refresh immediately
    if (hadOperations && !hasOperations) {
      console.log('All API operations completed - force refreshing documents and reports to clear status...');
      refreshDocuments();
      refreshReports();
    }

    window._hadApiOperations = hasOperations;
  }, [normalizedOperations.length, refreshDocuments, refreshReports]);

  // Update tab title to show operation count when tab is hidden
  useEffect(() => {
    const operationCount = combinedOperations.length;

    if (operationCount > 0) {
      document.title = `(${operationCount}) Policy Checker`;
    } else {
      document.title = 'Policy Checker';
    }

    // Cleanup on unmount
    return () => {
      document.title = 'Policy Checker';
    };
  }, [combinedOperations.length]);

  if (combinedOperations.length === 0 && !hasDocOperations) {
    return null;
  }

  const getOperationIcon = (type) => {
    switch (type) {
      case 'file_upload':
        return <FiUpload />;
      case 'file_processing':
      case 'kb_syncing':
        return <FiRefreshCw className="spinning-icon" />;
      case 'file_deleting':
        return <FiTrash2 />;
      case 'report_generating':
        return <FiFileText />;
      default:
        return <FiRefreshCw className="spinning-icon" />;
    }
  };

  const getOperationLabel = (operation) => {
    switch (operation.type) {
      case 'file_upload':
        return `Uploading ${operation.filename}`;
      case 'file_processing':
        return `Processing ${operation.filename}`;
      case 'file_deleting':
        return `Deleting ${operation.filename}`;
      case 'report_generating':
        return 'Generating report';
      case 'kb_syncing':
        return `Processing KB${operation.filename ? `: ${operation.filename}` : ''}`;
      default:
        return 'Operation in progress';
    }
  };

  const getStartedAt = (operation) => {
    const raw = operation.started_at
      || operation.startedAt
      || operation.created_at
      || operation.createdAt
      || operation.timestamp;

    if (!raw) return null;

    const date = typeof raw === 'number'
      ? new Date(raw * 1000)
      : new Date(raw);

    if (Number.isNaN(date.getTime())) return null;

    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getTriggeredBy = (operation) => {
    return operation.created_by_email
      || operation.created_by
      || operation.user_email
      || operation.owner_email
      || operation.owner
      || operation.email
      || null;
  };

  const formatProgress = (operation) => {
    return 'In progress...';
  };

  return (
    <div className="operations-indicator">
      <button
        className="operations-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-label={`${combinedOperations.length} operations in progress`}
      >
        <span className="operations-spinner"></span>
        <span className="operations-count">
          {combinedOperations.length} in progress
        </span>
        <span className={`operations-arrow ${isExpanded ? 'expanded' : ''}`}>
          &#9662;
        </span>
      </button>

      {isExpanded && (
        <div className="operations-dropdown">
          <div className="operations-header">Active Operations</div>
          <div className="operations-list">
            {combinedOperations.map(op => (
              <div key={op.id || `${op.type}-${op.filename || 'operation'}`} className={`operation-item operation-${op.type || 'unknown'}`}>
                <div className="operation-icon">{getOperationIcon(op.type)}</div>
                <div className="operation-details">
                  <div className="operation-name">{getOperationLabel(op)}</div>
                  <div className="operation-status">
                    {formatProgress(op)}
                  </div>
                  {(getStartedAt(op) || getTriggeredBy(op)) && (
                    <div className="operation-meta">
                      {getStartedAt(op) && <span>Started {getStartedAt(op)}</span>}
                      {getTriggeredBy(op) && <span>by {getTriggeredBy(op)}</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
